'use client';
import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, ExternalLink } from 'lucide-react';
import { SEBI_RISK_DISCLOSURES, SEBI_REGISTRATION, INVESTOR_GRIEVANCE, getMarketSession } from '@/lib/india-market';

export default function SEBIComplianceFooter() {
  const [session, setSession] = useState<string>('CLOSED');

  useEffect(() => {
    const update = () => setSession(getMarketSession(new Date()));
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, []);

  const sessionColors: Record<string, string> = {
    OPEN: 'text-positive',
    PRE_OPEN: 'text-warning',
    POST_CLOSE: 'text-info',
    CLOSED: 'text-muted-foreground',
  };

  return (
    <footer className="mt-6 border-t border-border bg-secondary/50">
      {/* Risk Disclosures */}
      <div className="px-4 lg:px-6 py-4 border-b border-border">
        <div className="flex items-start gap-2 mb-3">
          <AlertTriangle size={14} className="text-warning mt-0.5 flex-shrink-0" />
          <h3 className="text-xs font-bold text-warning uppercase tracking-wide">Risk Disclosures (SEBI Mandated)</h3>
        </div>
        <ul className="space-y-1.5">
          {SEBI_RISK_DISCLOSURES.map((d, i) => (
            <li key={`disc-${i}`} className="text-2xs text-muted-foreground flex items-start gap-1.5">
              <span className="text-muted-foreground/50 mt-0.5">•</span>
              {d}
            </li>
          ))}
        </ul>
      </div>

      {/* Market Hours */}
      <div className="px-4 lg:px-6 py-3 border-b border-border">
        <div className="flex flex-wrap items-center gap-4 text-2xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-foreground">NSE Market Hours (IST):</span>
            <span>Pre-Open: 09:00–09:15</span>
            <span className="text-border">|</span>
            <span>Regular: 09:15–15:30</span>
            <span className="text-border">|</span>
            <span>Post-Close: 15:40–16:00</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`font-semibold ${sessionColors[session] ?? 'text-muted-foreground'}`}>
              Current Session: {session.replace('_', ' ')}
            </span>
          </div>
        </div>
      </div>

      {/* Registration & Grievance */}
      <div className="px-4 lg:px-6 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield size={12} className="text-positive flex-shrink-0" />
            <p className="text-2xs text-muted-foreground">{SEBI_REGISTRATION}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-2xs text-muted-foreground">
            <span>{INVESTOR_GRIEVANCE}</span>
            <a
              href="https://scores.sebi.gov.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline"
            >
              SEBI SCORES <ExternalLink size={10} />
            </a>
            <a
              href="https://smartodr.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline"
            >
              ODR Portal <ExternalLink size={10} />
            </a>
          </div>
        </div>
        <p className="text-2xs text-muted-foreground/60 mt-2">
          © 2026 MyTrading. Registered in India. CIN: U67120MH2024PTC000000. 
          Investments in securities market are subject to market risks. Read all related documents carefully before investing.
          This platform does not provide investment advice. Past performance is not indicative of future results.
        </p>
      </div>
    </footer>
  );
}
